import { Injectable } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { PropertyStoreService } from './property-store.service';
import { PropertyService } from '../property.service';
import { Organisation } from '../../domain/corporate/organisation';

@Injectable({ providedIn: 'root' })
export class OrganisationContextStoreService {
   private organisationIdSubject = new BehaviorSubject<number | null>(null);
   readonly organisationId$ = this.organisationIdSubject.asObservable();

   private organisationsSubject = new BehaviorSubject<Organisation[]>([]);
   readonly organisations$ = this.organisationsSubject.asObservable();

   constructor(
      private propertyStore: PropertyStoreService,
      private propertyService: PropertyService
   ) {
      // When the app "currentProperty" changes, fetch orgs and set orgId
      this.propertyStore.currentProperty
         .pipe(
            map((p) => p?.id ?? null),
            distinctUntilChanged(),
            switchMap((propertyId) => {
               if (!propertyId) return of([] as Organisation[]);
               return this.propertyService
                  .getOrganisations(propertyId)
                  .pipe(catchError(() => of([] as Organisation[])));
            })
         )
         .subscribe((orgs) => {
            this.organisationsSubject.next(orgs);

            // keep existing selection if still valid; otherwise pick first
            const current = this.organisationIdSubject.value;
            const next =
               orgs.find((o) => o.id === current)?.id ?? orgs[0]?.id ?? null;

            this.organisationIdSubject.next(next);
         });
   }

   setOrganisationId(id: number | null): void {
      this.organisationIdSubject.next(id);
   }

   getCurrentOrganisationId(): number | null {
      return this.organisationIdSubject.value;
   }
}
